import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { ConfigProvider, Card, Typography, Table, Input, Space, Select, Modal, Tooltip, Button, message } from 'antd';
import { SearchOutlined, EditOutlined, SyncOutlined, LoadingOutlined } from '@ant-design/icons'
import { fetchRoutes, updateRouteDetails, fetchRouteSubcontractors, syncYetiRoutes } from '../api';
import SideMenu from '../components/SideMenu';
import { renderRoutesSummary } from '../components/YetiSync';

const RoutesPage = () => {
  const [loading, setLoading] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [selectedSubcontractor, setSelectedSubcontractor] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editRoute, setEditRoute] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('route_name');
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const [syncLoading, setSyncLoading] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncSummary, setSyncSummary] = useState(null);

  const [messageApi, contextHolder] = message.useMessage();

  const loadRoutes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchRoutes();
      console.log('routes data:', data);
      setRoutes(data);
    } catch (err) {
      console.error('Error fetching routes:', err);
      messageApi.error('Failed to load routes');
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  useEffect(() => {
    loadRoutes();
  }, [loadRoutes]);

  useEffect(() => {
    const filteredData = routes.filter(route => {
      const routeName = route.route_name || 'N/A';
      const zoneName = route.zone_name || 'N/A';
  
      return (
        routeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        zoneName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  
    setFilteredRoutes(filteredData);
  }, [searchQuery, routes]);
  
  const handleSyncClose = () => {
    setSyncSummary(null);
    setSyncOpen(false);
  };

  const handleYetiSync = async () => {
    setSyncLoading(true);
    try {
      const response = await syncYetiRoutes();
      setSyncSummary(response);
    } catch (error) {
      console.error("Failed to sync Routes with Yeti:", error);
      messageApi.error("Failed to sync Routes with Yeti");
    } finally {
      setSyncLoading(false);
      // The sync saves as it goes, so reload even if it failed partway
      loadRoutes();
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedRoutes = filteredRoutes.sort((a, b) => {
    const aValue = (a[orderBy] ?? 'N/A').toString();
    const bValue = (b[orderBy] ?? 'N/A').toString();
  
    return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
  });

  const handleEditClick = async (route) => {
    try {
      const subcontractorData = await fetchRouteSubcontractors(route.route_id);
  
      setEditRoute(route);
      setSelectedSubcontractor(route.default_subcontractor_id || null);
      setSubcontractors(subcontractorData);
  
      setEditOpen(true);
    } catch (error) {
      console.error('Error fetching subcontractors or route data:', error);
      messageApi.error('Failed to load route details');
    }
  };

  const handleSubcontractorChange = (value) => {
    setSelectedSubcontractor(value);
  };

  const saveRouteDetails = async () => {
    try {
      await updateRouteDetails(editRoute.route_id, {
        default_subcontractor_id: selectedSubcontractor,
      });
      loadRoutes();
      setEditOpen(false);
    } catch (error) {
      console.error('Error updating route details:', error);
      messageApi.error('Failed to update route details');
    }
  };

  const handleExpand = (expanded, route) => {
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, route.route_id]);
    } else {
      setExpandedRowKeys(expandedRowKeys.filter(key => key !== route.route_id));
    }
  };

  const expandedRowRender = (route) => {
    const siteColumns = [
      {
        title: 'Site Name',
        dataIndex: 'site_name',
        key: 'site_name',
      },
      {
        title: 'City',
        dataIndex: 'city',
        key: 'city',
      },
      {
        title: 'Client Name',
        dataIndex: 'client_name',
        key: 'client_name',
      },
    ];

    return (
      <Table
        columns={siteColumns}
        dataSource={route.sites}
        pagination={false}
        rowKey={(site) => site.site_id}
        style={{ marginLeft: '32px' }}
      />
    );
  };

  const routeColumns = [
    {
      title: 'Route Name',
      dataIndex: 'route_name',
      key: 'route_name',
      sorter: true,
      sortOrder: orderBy === 'route_name' && order,
      onHeaderCell: () => ({ onClick: () => handleRequestSort('route_name') }),
      render: (routeName) => (
        <Typography>{routeName}</Typography>
      ),
    },
    {
      title: 'Zone Name',
      dataIndex: 'zone_name',
      key: 'zone_name',
      width: '15%',
      sorter: true,
      sortOrder: orderBy === 'zone_name' && order,
      onHeaderCell: () => ({ onClick: () => handleRequestSort('zone_name') }),
      render: (zoneName) => (
        <Typography>{zoneName}</Typography>
      ),
    },
    {
      title: 'Default Subcontractor',
      key: 'default_subcontractor_name',
      sorter: true,
      sortOrder: orderBy === 'default_subcontractor_name' && order,
      onHeaderCell: () => ({ onClick: () => handleRequestSort('default_subcontractor_name') }),
      render: (record) => (
        <Typography>{record.default_subcontractor_name || 'None'}</Typography>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: '10%',
      fixed: 'right',
      render: (record) => (
        <EditOutlined
          onClick={() => handleEditClick(record)}
          style={{
            fontSize: '18px',
            cursor: 'pointer',
            color: 'lightgrey',
            transition: 'color 0.3s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#f57e1f')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'lightgrey')}
        />
      ),
    },
  ];
    
  const [tableHeight, setTableHeight] = useState('100vh - 72px - 16px');
  const titleRef = useRef(null);
  const actionsRef = useRef(null);
  const tableRef = useRef(null);

  useLayoutEffect(() => {
    const calculateTableHeight = () => {
      const headerHeight = 72;
      const titleHeight = titleRef.current ? titleRef.current.offsetHeight : 48;
      const actionsHeight = actionsRef.current ? actionsRef.current.offsetHeight : 32;
      const padding = 16;

      // Calculate height of the table header
      const tableHeaderHeight = tableRef.current ? tableRef.current.querySelector('.ant-table-thead').offsetHeight : 55;

      const totalHeightAroundTableRows =
        headerHeight +
        titleHeight +
        actionsHeight +
        padding * 4 +
        tableHeaderHeight;
      console.log(totalHeightAroundTableRows);
      setTableHeight(`calc(100vh - ${totalHeightAroundTableRows}px)`);
    };

    calculateTableHeight();

    window.addEventListener('resize', calculateTableHeight);
    return () => window.removeEventListener('resize', calculateTableHeight);
  }, []);

  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#F57D20', colorLinkHover: '#F57D20', colorLink: '#F57D20' } }}>
      {contextHolder}
      <div style={{ display: 'flex' }}>
        <SideMenu />

        <Card style={{ height: 'calc(100vh - 72px - 16px)', marginRight: '16px', overflow: 'hidden', boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)' }}>
          <Typography.Title ref={titleRef} level={2} style={{ margin: 0, paddingBottom: '10px' }}>
            Routes
          </Typography.Title>

          <div ref={actionsRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Input
              placeholder="Search Routes"
              value={searchQuery}
              onChange={handleSearchChange}
              style={{ width: '300px' }}
              prefix={<SearchOutlined style={{ color: '#d9d9d9' }} />}
            />
            <Tooltip title="Sync Routes with Yeti">
              <Button
                shape="circle"
                icon={<SyncOutlined />}
                onClick={() => setSyncOpen(true)}
                disabled={syncLoading}
              />
            </Tooltip>
          </div>

          <div ref={tableRef} style={{ overflow: 'hidden', marginTop: '16px' }}>
            <Table
              columns={routeColumns}
              dataSource={sortedRoutes}
              loading={loading}
              pagination={false}
              scroll={{ y: tableHeight }}
              sticky
              rowKey={(record) => record.route_id}
              expandable={{
                expandedRowKeys,
                onExpand: handleExpand,
                expandedRowRender: expandedRowRender,
              }}
            />
          </div>
        </Card>
      </div>

      <Modal
        title="Edit Route Details"
        open={editOpen}
        onOk={saveRouteDetails}
        onCancel={() => setEditOpen(false)}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ width: '100%', textAlign: 'left', fontSize: '12px', fontWeight: '500', fontFamily: "'Plus Jakarta Sans', sans-serif", marginLeft: '5px', color: "#4a4a4a"}}>Default Subcontractor</span>
          <Select
            value={selectedSubcontractor}
            onChange={handleSubcontractorChange}
            options={subcontractors.map((sub) => ({
              label: sub.name,
              value: sub.subcontractor_id,
            }))}
            style={{ width: '100%' }}
            placeholder="Select subcontractor"
          />
          <Typography variant="body2" color="textSecondary" style={{ marginTop: '10px' }}>
            Only subcontractors assigned to this route may be selected as the default subcontractor.
          </Typography>
        </div>
      </Modal>

      <Modal
        title={syncSummary ? "Yeti Route Sync Summary" : "Sync Routes with Yeti"}
        open={syncOpen}
        onCancel={handleSyncClose}
        width={700}
        footer={[
          <Button key="close" onClick={handleSyncClose} disabled={syncLoading}>
            Close
          </Button>,
          !syncSummary && (
            <Button key="start" type="primary" onClick={handleYetiSync} disabled={syncLoading}>
              Start Route Sync
            </Button>
          ),
        ]}
        styles={{
          body: {
            maxHeight: '70vh',
            overflowY: 'auto',
            paddingTop: syncSummary ? '16px' : '24px',
          },
        }}
      >
        {syncLoading ? (
          <Space direction="vertical" size="middle" style={{ width: '100%', textAlign: 'center', padding: '40px 0' }}>
            <LoadingOutlined style={{ fontSize: '48px', color: '#1890ff' }} spin />
            <Typography.Text style={{ fontSize: '16px' }}>Syncing routes with Yeti...</Typography.Text>
          </Space>
        ) : syncSummary ? (
          <div>
            {renderRoutesSummary(syncSummary)}
          </div>
        ) : (
          <div style={{ padding: '20px 0' }}>
            <Typography.Text style={{ fontSize: '14px', color: '#595959' }}>
              This will sync route data from Yeti, adding new routes and updating existing route records with the latest information.
            </Typography.Text>
            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#e6f7ff', borderRadius: '6px', borderLeft: '4px solid #1890ff' }}>
              <Typography.Text style={{ fontSize: '13px', color: '#0050b3' }}>
                💡 Tip: Sync subcontractors and sites first to avoid warnings about missing dependencies.
              </Typography.Text>
            </div>
          </div>
        )}
      </Modal>
    </ConfigProvider>
  );
};

export default RoutesPage;